import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom';
import imageOne from '../../resource/dana/dana-calendar/1.png'
import imageTwo from '../../resource/dana/dana-calendar/3.png'
import imageThree from '../../resource/dana/dana-calendar/2.png'
import BookingCalendar from '../Calendar/BookingCalendar';
import DanaRequestModal from '../Forms/DanaRequestModal';
import Swal from 'sweetalert2';
import apiClient from "@/api/client";

const DanaCalander = () => {
    const navigate = useNavigate();

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedDanaType, setSelectedDanaType] = useState(null);
    const [contactDetails, setContactDetails] = useState([]);

    // Single source of truth for calendar data
    const [calendarData, setCalendarData] = useState({
        breakfast: { booked: [], blocked: [] },
        lunch:     { booked: [], blocked: [] },
    });

    const [loading, setLoading] = useState(true);          // contact details
    const [calendarLoading, setCalendarLoading] = useState(true);

    // Create refs for both calendars
    const breakfastCalendarRef = useRef(null);
    const lunchCalendarRef = useRef(null);

    // Function to render contact value with appropriate link based on type
    const renderContactValue = (contact) => {
        const value = contact.value;
        const type = contact.type || 'phone';

        switch(type) {
            case 'email':
                return (
                    <a
                        href={`mailto:${value}`}
                        style={{ color: '#c49b36', textDecoration: 'none' }}
                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                        {value}
                    </a>
                );
            case 'web':
                return (
                    <a
                        href={value}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#c49b36', textDecoration: 'none' }}
                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                        {value.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                    </a>
                );
            case 'phone':
            default:
                const phoneNumber = value.replace(/[^0-9+]/g, '');
                return (
                    <a
                        href={`tel:${phoneNumber}`}
                        style={{ color: '#c49b36', textDecoration: 'none' }}
                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                        {value}
                    </a>
                );
        }
    };

    // Fetch contact details from API
    useEffect(() => {
        const fetchContactDetails = async () => {
            try {
                const response = await apiClient.get('/contact-setting');
                const settings = response.data.settings || {};
                const danaContacts = settings.dana || [];
                setContactDetails(danaContacts);
            } catch (error) {
                console.error('Error fetching contact details:', error);
                setContactDetails([]);
            } finally {
                setLoading(false);
            }
        };
        fetchContactDetails();
    }, []);

    // ---- Single aggregate fetch -------------------------------------------
    const fetchCalendarData = useCallback(async (isRefresh = false) => {
        if (!isRefresh) setCalendarLoading(true);

        try {
            const res = await apiClient.get('/get-dana-calendar-summary');
            const d = res.data?.data || {};

            setCalendarData({
                breakfast: {
                    booked:  d.breakfast?.booked  ?? [],
                    blocked: d.breakfast?.blocked ?? [],
                },
                lunch: {
                    booked:  d.lunch?.booked  ?? [],
                    blocked: d.lunch?.blocked ?? [],
                },
            });
        } catch (err) {
            console.error('Error fetching dana calendar summary:', err);
        } finally {
            if (!isRefresh) setCalendarLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCalendarData(false);
    }, [fetchCalendarData]);

    const handleClick = (event) => {
        const pathMap = { danaRequest: '/danaRequest' };
        const path = event.currentTarget.id;
        const targetPath = pathMap[path];
        if (targetPath) navigate(targetPath);
    };

    const handleDateClick = (date, danaType) => {
        const calendarRef =
            danaType === "breakfast" ? breakfastCalendarRef.current : lunchCalendarRef.current;

        if (calendarRef && calendarRef.getBookedDates) {
            const bookedDates = calendarRef.getBookedDates();
            if (bookedDates.has(date)) {
                Swal.fire({
                    icon: "warning",
                    title: "Date Unavailable",
                    text: `This date is already booked for ${danaType} dana. Please select another date.`,
                    confirmButtonColor: "#c49b36",
                });
                return;
            }
        }

        setSelectedDate(date);
        setSelectedDanaType(danaType);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedDate(null);
        setSelectedDanaType(null);
    };

    const handleSuccessfulSubmit = async () => {
        await Swal.fire({
            icon: "success",
            title: "Booking Successful!",
            text: "Your dana request has been submitted successfully. The calendar will now update.",
            confirmButtonColor: "#c49b36",
            timer: 2000,
            showConfirmButton: true
        });
        await fetchCalendarData(true);
        handleCloseModal();
    };

    const handleBookingError = async (errorMessage) => {
        await fetchCalendarData(true);
        Swal.fire({
            icon: "info",
            title: "Date Already Booked",
            text: errorMessage + " The calendar has been updated to show current availability.",
            confirmButtonColor: "#c49b36",
        });
    };

    if (loading || calendarLoading) {
        return (
            <div className="danaClander-page inside-pages">
                <div className="right-box">
                    <img src={imageOne} alt="Right Top Image" className="right-image" width={300} />
                </div>
                <div className="text-center p-4">
                    <h1 className='font-main dark-gold p-4'>Loading calendar data...</h1>
                </div>
            </div>
        );
    }

    return (
        <div className="danaClander-page inside-pages">
            <div className="right-box">
                <img src={imageOne} alt="Right Top Image" className="right-image" width={300} />
            </div>

            <h1 className='font-main dark-gold p-4'>
                Dana Calendar
            </h1>

            <div className="poem-1 mt-6 p-4">
                <div>
                    <p className="h4-bold">With hands joined in reverence, </p>
                    <p className="h4-bold">one supports the Sangha,</p>
                    <p className="h4-bold">Accumulating deeds of merit  </p>
                    <p className="h4-bold">that lead to the path of wealth,</p>

                    <p className="h4-bold">A life of pleasantness and happiness </p>
                    <p className="h4-bold">in this world and the next.</p>
                    <p className="h4-bold">The merit one has cultivated protects them,</p>
                    <p className="h4-bold"> like a true friend,</p>

                    <p className="h4-bold">A comrade for future existences, </p>
                    <p className="h4-bold">guarding them through life.</p>
                    <p className="h4-bold">Blessings from the divine realms </p>
                    <p className="h4-bold">come to those who walk this virtuous path.</p>
                </div>
            </div>

            <div className="calander-box mt-6 p-4">
                <div className="cl-right">
                    <h1 className='font-second dark-gold p-4 text-center'>Breakfast Dana</h1>
                    <div className="calnder text-center mt-5">
                        <BookingCalendar
                            ref={breakfastCalendarRef}
                            bookedDates={calendarData.breakfast.booked}
                            blockedDates={calendarData.breakfast.blocked}
                            filterType="breakfast"
                            onDateClick={handleDateClick}
                            showRefreshButton={true}
                            onRefresh={() => fetchCalendarData(true)}
                        />
                    </div>
                </div>
                <div className="cl-left">
                    <h1 className='font-second dark-gold p-4 text-center'>Lunch Dana</h1>
                    <div className="calnder text-center mt-5">
                        <BookingCalendar
                            ref={lunchCalendarRef}
                            bookedDates={calendarData.lunch.booked}
                            blockedDates={calendarData.lunch.blocked}
                            filterType="lunch"
                            onDateClick={handleDateClick}
                            showRefreshButton={true}
                            onRefresh={() => fetchCalendarData(true)}
                        />
                    </div>
                </div>
            </div>

            <div className="p-4 mt-6 text-center">
                <p className="h4 ">The Dana Calendar for this month is shown as above. The dates with red borders or red backgrounds are already booked.</p>
                <p className="h4 ">It is appreciated if a donor can offer Dana annually and can select a date of the choice.</p>

                <div className="mt-6">
                   <h2 className='intro-text '>May your kindness and generosity be a wellspring of joy.</h2>
                   <h2 className='intro-text '>May we all live</h2>
                   <h2 className='intro-text '>free from hunger – in harmony,</h2>
                   <h2 className='intro-text '>safety and peace.</h2>
                </div>

                <div className="mt-6 calander-box align-item-center">
                    <div className="c-details text-left">
                        <h2 className='intro-text '>Contact Details</h2>
                        <div className="mt-4">
                            {contactDetails.length === 0 ? (
                                <p className="font-p" style={{ color: '#999' }}>
                                    No contact details available.
                                </p>
                            ) : (
                                contactDetails.map((contact, index) => (
                                    <p key={index} className="font-p">
                                        {contact.label}: {renderContactValue(contact)}
                                        {contact.note && <span style={{ color: '#666', fontSize: '0.9rem' }}> ({contact.note})</span>}
                                    </p>
                                ))
                            )}
                        </div>
                    </div>
                    <div className="d-button">
                        <img src={imageTwo} alt="Right Top Image" className="right-image curser-pointer" width={300} id='danaRequest' onClick={handleClick}  />
                    </div>
                </div>
            </div>

            <div className="bottom-box mt-5">
                <img src={imageThree} alt="bottom-image" width={600} />
            </div>

            <DanaRequestModal
                isOpen={modalOpen}
                onClose={handleCloseModal}
                selectedDate={selectedDate}
                danaType={selectedDanaType}
                onSuccess={handleSuccessfulSubmit}
                onError={handleBookingError}
            />
        </div>
    );
};

export default DanaCalander;